import currency from 'currency.js';
import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { IReportData } from '../../report.types';

import brand from 'assets/styles/variables/brand';
import fonts from 'assets/styles/variables/fonts';

interface IComponentProps {
	reportData?: IReportData[];
}

const StyledResults = styled.div`
	display: grid;
	grid-auto-flow: row;
	overflow: auto;
`;

const StyledHeadings = styled.div`
	display: flex;
	align-items: center;
	background: ${brand.black};
	background-size: cover;
	font-size: ${fonts.sizes.med};
	line-height: ${fonts.line_height.large};
	user-select: none;
	padding: 7px 5px;
`;

const StyledHeading = styled.div`
	color: ${brand.white};
	font-size: ${fonts.sizes.med};
	font-weight: ${fonts.weights.medium};
	display: flex;
	align-items: center;
	min-width: 85px;
	margin: 0 10px 0 0;
	user-select: none;
	width: calc(10% - 20px);

	:focus {
		outline: none;
	}

	&:nth-child(1) {
		min-width: 120px;
	}

	&:nth-child(8) {
		min-width: 275px;
	}

	&:nth-child(2),
	&:nth-child(10) {
		min-width: 70px;
	}

	&:nth-child(3),
	&:nth-child(9),
	&:nth-child(11) {
		min-width: 50px;
	}
`;

const StyledData = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;

	li {
		padding: 7px 5px;
		margin: 0;
		display: flex;

		&:nth-child(2n + 2) {
			background: ${brand.neutral};
		}

		> div {
			display: flex;
			align-items: center;
			min-width: 85px;
			margin: 0 10px 0 0;
			user-select: none;
			width: calc(10% - 20px);

			:focus {
				outline: none;
			}

			&:nth-child(1) {
				min-width: 120px;
			}

			&:nth-child(8) {
				min-width: 275px;
			}

			&:nth-child(2),
			&:nth-child(10) {
				min-width: 70px;
			}

			&:nth-child(3),
			&:nth-child(9),
			&:nth-child(11) {
				min-width: 50px;
			}
		}
	}
`;

const TransactionReportResults: FunctionComponent<IComponentProps> = ({
	reportData,
}) => {
	const headings: string[] = [
		'dateTime',
		'type',
		'card',
		'merchantId',
		'account',
		'brand',
		'site',
		'transId',
		'gross',
		'transFee',
		'net',
	];

	const mappedHeadings = headings.map((heading: string) => (
		<StyledHeading key={heading}>
			<FormattedMessage id={`reports.results.headings.${heading}`} />
		</StyledHeading>
	));

	return (
		<StyledResults>
			<StyledHeadings>{mappedHeadings}</StyledHeadings>
			<StyledData>
				{reportData?.map((data) => (
					<li>
						<div>
							{data?.date} {data?.time}
						</div>
						<div>{data?.type}</div>
						<div>{data?.card}</div>
						<div>{data?.merchantId}</div>
						<div>{data?.account}</div>
						<div>{data?.brand}</div>
						<div>{data?.site}</div>
						<div>{data?.identifier}</div>
						<div>
							{data?.gross
								? currency(data.gross).format({ symbol: '£' })
								: '£0.00'}
						</div>
						<div>
							{data?.fee
								? currency(data.fee).format({ symbol: '£' })
								: '£0.00'}
						</div>
						<div>
							{data?.net
								? currency(data.net).format({ symbol: '£' })
								: '£0.00'}
						</div>
					</li>
				))}
			</StyledData>
		</StyledResults>
	);
};

export default TransactionReportResults;
